/**
 * Tooltip Component
 * Reusable tooltip component with hover and click triggers
 * Following modern UI patterns with accessibility support
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../../lib/utils';

interface TooltipProps {
  content: string;
  children: React.ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
  disabled?: boolean;
}

/**
 * Tooltip component that shows on hover or focus
 * Accessible with ARIA attributes and keyboard navigation
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  delay = 300,
  className,
  disabled = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTimeout, setShowTimeout] = useState<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  /**
   * Show tooltip with delay
   */
  const handleShow = (): void => {
    if (disabled) return;
    
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setShowTimeout(timeout);
  };

  /**
   * Hide tooltip immediately
   */
  const handleHide = (): void => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
    setIsVisible(false);
  };

  /**
   * Handle click outside to close tooltip
   */
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent): void => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        handleHide();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (showTimeout) {
        clearTimeout(showTimeout);
      }
    };
  }, [showTimeout]);

  /**
   * Clone child element with event handlers
   */
  const child = children as React.ReactElement<{
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
  }>;
  const childWithProps = React.cloneElement(child, {
    onMouseEnter: handleShow,
    onMouseLeave: handleHide,
    onFocus: handleShow,
    onBlur: handleHide
  });

  /**
   * Position classes based on side
   */
  const getPositionClasses = (): string => {
    switch (side) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  /**
   * Arrow position classes
   */
  const getArrowClasses = (): string => {
    switch (side) {
      case 'top':
        return 'top-full left-1/2 transform -translate-x-1/2';
      case 'bottom':
        return 'bottom-full left-1/2 transform -translate-x-1/2';
      case 'left':
        return 'left-full top-1/2 transform -translate-y-1/2';
      case 'right':
        return 'right-full top-1/2 transform -translate-y-1/2';
      default:
        return 'top-full left-1/2 transform -translate-x-1/2';
    }
  };

  /**
   * Arrow rotation
   */
  const getArrowRotation = (): string => {
    switch (side) {
      case 'top':
        return 'rotate-45 transform -translate-y-1/2';
      case 'bottom':
        return 'rotate-45 transform translate-y-1/2';
      case 'left':
        return 'rotate-45 transform -translate-x-1/2';
      case 'right':
        return 'rotate-45 transform translate-x-1/2';
      default:
        return 'rotate-45 transform -translate-y-1/2';
    }
  };

  return (
    <div className="relative inline-block">
      {childWithProps}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={cn(
              'absolute z-50',
              getPositionClasses(),
              'px-3 py-2',
              'bg-gray-900 dark:bg-gray-100',
              'text-white dark:text-gray-900',
              'text-xs font-medium',
              'rounded-lg shadow-lg',
              'whitespace-nowrap',
              'pointer-events-none',
              className
            )}
            role="tooltip"
            aria-hidden={!isVisible}
          >
            {content}
            {/* Tooltip Arrow */}
            <div className={cn('absolute', getArrowClasses())}>
              <div
                className={cn(
                  'w-2 h-2',
                  'bg-gray-900 dark:bg-gray-100',
                  getArrowRotation()
                )}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Default export
 */
export default Tooltip;

